import * as React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Typography, CardActionArea, Box, Chip, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const getCategory = (toolEnum) => {
  const categories = {
    'Preprocessing': ['tokenizer', 'sentence-splitter', 'deasciifier', 'deasciifier_v2', 'sent_splitter_v2', 'tokenizer_version2'],
    'Morphology': ['lemmatizer', 'morphological_analyzer', 'morphological_disambiguator', 'morphological_analyzer_disambiguator'],
    'Syntax': ['boun_pars'],
    'Analysis': ['ermi', 'ner', 'question_answering', 'relx', 'sentiment_embeddings', 'bounti_sentiment_analyzer', 'targeted-sentiment-analysis', 'trvectors', 'postagger', 'spellcorrection', 'vowelizer'],
    'Generation': ['bounsumm']
  };
  for (const [cat, enums] of Object.entries(categories)) {
    if (enums.includes(toolEnum)) return cat;
  }
  return 'General';
};

export default function ModernToolCard({ tool }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const category = getCategory(tool.enum);

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid #e0e0e0',
        borderRadius: 3,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px rgba(0,51,102,0.15)',
          borderColor: '#003366',
          '& .MuiTypography-h6': {
            color: '#003366',
          },
          '& .arrow-icon': {
            transform: 'translateX(4px)',
            color: '#003366',
          }
        }
      }}
    >
      <CardActionArea 
        component={Link} 
        to={'/' + tool.enum}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Chip 
              label={category} 
              size="small" 
              sx={{ 
                bgcolor: '#f0f7ff', 
                color: '#003366', 
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: '6px'
              }} 
            />
          </Box>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 700, 
              mb: 1.5, 
              lineHeight: 1.2,
              transition: 'color 0.2s'
            }}
          >
            {tool.general_info[lang].name}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.6,
              mb: 2
            }}
          >
            {tool.general_info[lang].description}
          </Typography>
        </CardContent>
        <Divider sx={{ mx: 3, opacity: 0.6 }} />
        <Box sx={{ p: 2, px: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Typography variant="button" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 700 }}>
            {t('trydemo')}
          </Typography>
          <ChevronRightIcon 
            className="arrow-icon"
            sx={{ 
              fontSize: '1.2rem', 
              ml: 0.5, 
              color: 'text.disabled',
              transition: 'all 0.2s'
            }} 
          />
        </Box>
      </CardActionArea>
    </Card>
  );
}
